import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVav } from './useVav.ts';
import { useT } from './i18n.tsx';
import { useBackButton } from './useBackButton.ts';
import { CallManager, requestAnswer } from './call.ts';
import { Onboarding } from './components/Onboarding.tsx';
import { ApproveLinkDialog } from './components/ApproveLinkDialog.tsx';
import { CallOverlay } from './components/CallOverlay.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { Thread } from './components/Thread.tsx';
import { activeProfileId, setActiveProfile } from './profiles.ts';
import { shareSubscriptionWith } from './push.ts';
import { notifyIncoming } from './notify.ts';
import { BackgroundProfiles, type ProfileSummary } from './backgroundProfiles.ts';

/**
 * In dev the Vite proxy puts the API on the same origin, so a relative base
 * works and there is no CORS or mixed-content story to manage. Override with
 * VITE_SERVER_URL when the app and server are deployed separately.
 */
const SERVER_URL = import.meta.env.VITE_SERVER_URL ?? window.location.origin;

/**
 * Owns which profile is active and keeps the others running.
 *
 * The switch is a remount, not a state update: `key` on <Workspace> throws away
 * every client, socket and store belonging to the old profile rather than
 * trying to repoint them. Repointing is how one profile's session ends up
 * decrypting with another's ratchet.
 */
export default function App() {
  const [profileId, setProfileId] = useState(activeProfileId);
  const [summaries, setSummaries] = useState<ProfileSummary[]>([]);
  const [background] = useState(() => new BackgroundProfiles(SERVER_URL));

  useEffect(() => background.subscribe(setSummaries), [background]);

  useEffect(() => {
    void background.sync(profileId).then(() =>
      // Every profile re-registers the browser's one subscription on every
      // boot. Sharing only at the moment the user pressed "turn on" left any
      // profile created afterwards without a push token — unreachable by
      // push, which for a frozen phone means unreachable at all.
      shareSubscriptionWith(background.clients()),
    );
  }, [background, profileId]);

  // A push wakes the page; every profile drains, not just the visible one.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const kind = (event.data as { kind?: string })?.kind;
      if (kind === 'push-wake') void background.drainAll();
      // The lock-screen Answer button, relayed by the worker to a page that
      // was already open.
      if (kind === 'call-answer') requestAnswer();
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [background]);

  // The Answer button on a phone where the app was closed: the worker opened
  // this page with the intent in the URL. Consume it and clean the address.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('call') === 'answer') {
      requestAnswer();
      params.delete('call');
      const query = params.toString();
      window.history.replaceState(null, '', query ? `/?${query}` : '/');
    }
  }, []);

  /**
   * A scanned linking QR lands here: the camera opened /?approve=<offer>.
   * Read at the top level so the code survives a profile switch — with
   * several profiles, the one that is open when the user taps Approve is the
   * account the new device joins, and switching first must not lose the scan.
   */
  const [approveCode, setApproveCode] = useState<string>();
  const [approveStranded, setApproveStranded] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wrapped = params.get('approve');
    if (!wrapped) return;
    setApproveCode(decodeURIComponent(wrapped));
    params.delete('approve');
    const query = params.toString();
    window.history.replaceState(null, '', query ? `/?${query}` : '/');
  }, []);

  /**
   * The scan can land in the wrong place: the active profile might be an
   * unregistered shell, whose onboarding screen would swallow the code in
   * silence — a dead end with no profile switcher on it. If any profile in
   * this browser holds an account, switch to it and let the dialog appear;
   * if none does (the camera opened a browser that has never seen the
   * account), say so instead of showing a signup form.
   */
  useEffect(() => () => background.stop(), [background]);

  const switchProfile = useCallback((id: string) => {
    setActiveProfile(id);
    setProfileId(id);
  }, []);

  /**
   * Not a one-shot: the background clients boot asynchronously, so at the
   * moment the stranded signal first fires the registered profile may simply
   * not have connected yet. Keep watching — the moment one exists, switch to
   * it; the notice shows only while there is truly nothing to switch to.
   */
  const [strandedRequested, setStrandedRequested] = useState(false);
  const onApproveStranded = useCallback(() => setStrandedRequested(true), []);
  useEffect(() => {
    if (!strandedRequested) return;
    const registered = background.registeredIds();
    if (registered[0]) {
      setStrandedRequested(false);
      setApproveStranded(false);
      switchProfile(registered[0]);
    } else {
      setApproveStranded(true);
    }
  }, [strandedRequested, summaries, background, switchProfile]);

  return (
    <>
      <Workspace
        key={profileId}
        profileId={profileId}
        profiles={summaries}
        onSwitchProfile={switchProfile}
        onPushEnabled={() => void shareSubscriptionWith(background.clients())}
        approveCode={approveCode}
        onApproveDone={() => setApproveCode(undefined)}
        onApproveStranded={onApproveStranded}
        approveStranded={approveStranded}
      />
      {/* Calls to profiles that are not on screen still ring this device.
          Answering happens in place — no profile switch, which would tear the
          call down with the tree it lives in. */}
      {background.callManagers().map(({ profileId: id, label, client, manager }) => (
        <CallOverlay
          key={id}
          manager={manager}
          client={client}
          /* The first profile predates labels; its account name still tells
             the user which of their identities is being called. */
          profileLabel={label || client.account?.displayName || client.account?.username || ''}
        />
      ))}
    </>
  );
}

function Workspace({
  profileId,
  profiles,
  onSwitchProfile,
  onPushEnabled,
  approveCode,
  onApproveDone,
  onApproveStranded,
  approveStranded,
}: {
  profileId: string;
  profiles: ProfileSummary[];
  onSwitchProfile: (id: string) => void;
  onPushEnabled: () => void;
  approveCode?: string;
  onApproveDone: () => void;
  onApproveStranded: () => void;
  approveStranded: boolean;
}) {
  const vav = useVav(SERVER_URL, profileId);
  const t = useT();

  // The active profile too: switched-to or freshly created, it must hold the
  // push token or a frozen phone cannot be rung for it.
  useEffect(() => {
    if (vav.client) void shareSubscriptionWith([vav.client]);
  }, [vav.client]);


  /**
   * Notify for a message that arrives while the app is in the background.
   *
   * The service worker can only raise a generic "new message" — the push it
   * receives is contentless by design. Here the message has been decrypted, so
   * the notification can carry the sender's name and a preview without any of
   * that having left the device.
   *
   * `contactsById` is read through a ref so a new contact does not resubscribe
   * the listener and duplicate every notification.
   */
  const contactsRef = useRef(vav.contactsById);
  contactsRef.current = vav.contactsById;

  useEffect(() => {
    const client = vav.client;
    if (!client) return;
    return client.on('message', (message) => {
      if (message.outgoing) return;
      const name = contactsRef.current[message.senderId]?.displayName ?? t('quote.them');
      const content = message.content as { text?: string; kind?: string };
      notifyIncoming(message, name, content.text || t('thread.attachment'));
    });
  }, [vav.client, t]);

  // The device Back gesture should leave the thread, not the app.
  const closeThread = useCallback(() => vav.setActiveId(undefined), [vav.setActiveId]);
  useBackButton(Boolean(vav.activeId), closeThread);

  /**
   * One call manager per client. It lives at the workspace so a call survives
   * navigating between conversations; the profile-switch remount tears it down
   * with everything else, which is right — the ratchet it signals over is gone.
   */
  const callManager = useMemo(() => (vav.client ? new CallManager(vav.client) : undefined), [vav.client]);
  useEffect(() => () => callManager?.dispose(), [callManager]);

  // A scanned approval landing on a profile with no account cannot be
  // approved here; hand the decision back up instead of a silent dead end.
  // Lives above the early returns because hooks must.
  useEffect(() => {
    if (approveCode && vav.phase !== 'loading' && (vav.phase === 'onboarding' || !vav.client)) {
      onApproveStranded();
    }
  }, [approveCode, vav.phase, vav.client, onApproveStranded]);

  if (vav.phase === 'loading') {
    return (
      <div className="splash">
        <div className="splash__mark">🔐</div>
        <p className="muted">{t('app.opening')}</p>
      </div>
    );
  }

  // A failed boot has to say so. The spinner above is indistinguishable from a
  // hang, and on a real device the keystore or the database genuinely can fail.
  if (vav.phase === 'failed') {
    return (
      <div className="splash">
        <div className="splash__mark" aria-hidden="true">
          ⚠︎
        </div>
        <h2>{t('app.failed.title')}</h2>
        {vav.error && <p className="muted">{vav.error}</p>}
        <button className="primary" onClick={() => window.location.reload()}>
          {t('app.failed.retry')}
        </button>
      </div>
    );
  }

  if (vav.phase === 'onboarding' || !vav.client) {
    return (
      <Onboarding
        onRegister={vav.register}
        error={vav.error}
        inviter={vav.inviter}
        serverUrl={SERVER_URL}
        profileId={profileId}
        notice={approveStranded ? t('link.approve.stranded') : undefined}
      />
    );
  }

  return (
    <div className={`app ${vav.activeId ? 'app--threadOpen' : ''}`}>
      <Sidebar
        client={vav.client}
        conversations={vav.conversations}
        activeId={vav.activeId}
        onSelect={vav.setActiveId}
        contactsById={vav.contactsById}
        presence={vav.presence}
        status={vav.status}
        profileId={profileId}
        profiles={profiles}
        onSwitchProfile={onSwitchProfile}
        onPushEnabled={onPushEnabled}
      />

      {vav.activeConversation ? (
        <Thread
          client={vav.client}
          conversations={vav.conversations}
          conversation={vav.activeConversation}
          messages={vav.messages}
          contactsById={vav.contactsById}
          presence={vav.presence}
          typing={vav.typing}
          onSendText={vav.sendText}
          onSendFile={vav.sendFile}
          onSendVoice={vav.sendVoice}
          onBack={() => vav.setActiveId(undefined)}
          onStartCall={
            callManager
              ? (media) =>
                  void callManager.start(
                    vav.activeConversation!.id,
                    media,
                    vav.activeConversation!.peerId,
                  )
              : undefined
          }
        />
      ) : (
        <section className="thread thread--empty">
          <div className="emptyState">
            <div className="emptyState__mark">🔐</div>
            <h2>{t('app.empty.title')}</h2>
            <p className="muted">{t('app.empty.body')}</p>
          </div>
        </section>
      )}

      {vav.identityAlerts.map((alert) => (
        <div key={alert.userId} className="alertBar" role="alert">
          {/* One sentence, one key. Splitting it around the name would put the
              name where English wants it and nowhere else. */}
          <span>
            {t('app.identityAlert.body', {
              name: vav.contactsById[alert.userId]?.displayName ?? alert.userId,
            })}
          </span>
          <span className="alertBar__actions">
            <button onClick={() => void vav.dismissIdentityAlert(alert.userId, true)}>
              {t('app.identityAlert.accept')}
            </button>
            <button className="ghost" onClick={() => void vav.dismissIdentityAlert(alert.userId, false)}>
              {t('app.identityAlert.dismiss')}
            </button>
          </span>
        </div>
      ))}

      {vav.error && (
        <div className="toast" role="status" onClick={vav.clearError}>
          {vav.error}
        </div>
      )}

      {callManager && (
        <CallOverlay manager={callManager} client={vav.client} contactsById={vav.contactsById} />
      )}

      {approveCode && (
        <ApproveLinkDialog client={vav.client} code={approveCode} onClose={onApproveDone} />
      )}
    </div>
  );
}
